import {
  IStateDriver,
  IUpdateDriver,
  ICoordinates,
  ICreateDriver,
  IHistoryDriverTrips,
  IDriverSearchObject,
  IDriverFilters,
  IDriverSessionFilters,
  IDriverSessionHistoryFilters,
  IDriverAccumulatedHours,
  IDriverAccumulatedHoursRequest,
  IDriverAddSessionObject,
  IDriverEditSessionObject,
  IDriverDeleteSessionObject,
  IDriverMetricsFilters,
  IDriverPushNotificationPayload,
  IComputeOperatorDriveTimes
} from '../types'
import { serverApi } from '../utils/request'
import Paths from '../utils/paths'
import { IDriverScheduleDTO } from 'src/components/drivers/DriverEditSchedule'
import { stringList } from 'aws-sdk/clients/datapipeline'

export interface IFetchDrivers {
  drivers: { [key: string]: IStateDriver }
  total: number
}

export interface IDriverAddress {
  id?: string
  city: string
  coordinates?: {
    longitude: number
    latitude: number
  }
  country: string
  fullAddress?: string
  isNew?: boolean
  placeName?: string
  state: string
  street: string
  streetNumber?: string
  timeZone?: string
  unit?: string
  zip: string
}

export interface IDriverUpdateAddressPayload {
  operatorId: string
  locations: IDriverAddress[]
}

function serializeDriver(driver: IStateDriver): IDriverSearchObject {
  const email = driver.user.email ? driver.user.email.emailAddress : ''
  const phoneObj = driver.user.phones ? driver.user.phones[0] : null
  const phone = phoneObj ? phoneObj.phoneNumber : ''
  const countryCode = phoneObj ? phoneObj.countryCode : ''
  // const dateOfBirth = driver.user.birthDate ? new Date(driver.user.birthDate).toISOString() : new Date().toISOString()

  return {
    id: driver.id,
    name: `${driver.user.firstName} ${driver.user.lastName}`,
    firstName: driver.user.firstName,
    lastName: driver.user.lastName,
    isRemoved: driver.isRemoved,
    isArchived: driver.isArchived,
    isActive: driver.user.isActive,
    isAvailableForBackToBack: driver.isAvailableForBackToBack,
    email,
    phone,
    countryCode,
    username: driver.user.username,
    birthDate: driver.user.birthDate ? driver.user.birthDate : '',
    vehicle: driver.vehicle,
    productIds: driver.products.map(pp => pp.id),
    products: driver.products,
    providers: driver.providers,
    organizationIds: driver.organizations.map(o => o.id),
    organizations: driver.organizations,
    driversLicense: driver.driversLicense ? driver.driversLicense : undefined,
    darkmode: driver.darkmode
  }
}

class DriversService {
  private static DEFAULT_PAGE = 1
  private static DEFAULT_RECORDS_PER_PAGE = 10

  public async getMany(dto: IDriverFilters): Promise<any> {
    const resp: any =
      dto.serviceIds && !!dto.serviceIds.length
        ? await serverApi.post(Paths.driver.search, { ...dto }).catch(e => {
            throw new Error(e.data)
          })
        : await serverApi.get(Paths.driver.search, { params: dto }).catch(e => {
            throw new Error(e.data)
          })
    return resp.data
  }

  public async getSessionHistory(dto: IDriverSessionFilters): Promise<any> {
    const resp: any = await serverApi.get(Paths.driver.sessionHistory, {
      params: dto
    })
    return resp.data
  }

  public async getSessionHistoryV2(dto: IDriverSessionFilters): Promise<any> {
    const resp: any = await serverApi.get(Paths.driver.sessionHistoryV2, {
      params: dto
    })
    return resp.data
  }

  public async getOperatorMetrics(dto: IDriverMetricsFilters): Promise<any> {
    // NOTE: Under performance optimization
    const resp: any = await serverApi.get(Paths.trips.getOperatorMetrics, {
      params: dto
    })
    return resp.data
  }

  public async getManyTripsPerOperatorV2(dto: IDriverSessionHistoryFilters): Promise<any> {
    const resp: any = await serverApi.get(Paths.trips.getManyTripsPerOperatorV2, {
      params: dto
    })
    return resp.data
  }

  public async createDriverSession(request: IDriverAddSessionObject): Promise<any> {
    const result = await serverApi.post<any[]>(Paths.driver.createSession, request).catch(e => {
      throw new Error(e.data)
    })

    return result.data
  }

  public async editDriverSession(request: IDriverEditSessionObject): Promise<any> {
    return await serverApi.put<any[]>(Paths.driver.editSession, request).catch(e => {
      throw new Error(e.data)
    })
  }

  public async deleteDriverSession(request: IDriverDeleteSessionObject): Promise<any> {
    return await serverApi.put<any[]>(Paths.driver.deleteSession, request).catch(e => {
      throw new Error(e.data)
    })
  }

  public async getCheckInCheckOutV1(filters: IDriverSessionHistoryFilters): Promise<any> {
    const resp: any = await serverApi.get(Paths.driver.sessionHistory, {
      params: filters
    })
    return resp.data
  }

  public async getCheckInCheckOut(filters: IDriverSessionHistoryFilters): Promise<any> {
    const resp: any = await serverApi.get(Paths.driver.sessionHistoryV2, {
      params: filters
    })
    return resp.data
  }

  public async getManyTripsPerOperator(filters: IDriverSessionHistoryFilters): Promise<any> {
    const resp: any = await serverApi.get(Paths.trips.getManyTripsPerOperator, {
      params: filters
    })
    return resp.data
  }

  public async fetchDrivers(
    archived: boolean,
    page: number = DriversService.DEFAULT_PAGE,
    recordsPerPage: number = DriversService.DEFAULT_RECORDS_PER_PAGE
  ): Promise<any> {
    return serverApi
      .get<any>(Paths.driver.index, {
        params: { archived, page, recordsPerPage }
      })
      .then(({ data }) => data)
  }

  public async addProductsToDriver(driverId: string, productIds: string[], organizationId: string) {
    return serverApi
      .post(`${Paths.driver.saveProducts}`, { productIds, id: driverId, organizationId })
      .then(({ data }) => data)
  }

  public async removeProduct(driverId: string, productId: string) {
    const path = Paths.driver.removeProduct.replace(':id', driverId).replace(':productId', productId)
    return serverApi.delete(path).then(({ data }) => data)
  }

  public async archiveDriver(driverId: string): Promise<any> {
    const path = Paths.driver.archive.replace(':id', driverId)
    return serverApi.post(path)
  }

  public async unarchiveDriver(driverId: string): Promise<any> {
    const path = Paths.driver.unarchive.replace(':id', driverId)
    return serverApi.post(path)
  }

  public driverTripsHistoryDriver(): Promise<IHistoryDriverTrips[]> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        console.log('TODO: add driver trip history')
      }, 1000)
    })
  }

  public async getDriverById(id: string): Promise<IDriverSearchObject> {
    try {
      const result = await serverApi.get(`${Paths.driver.getOneById}/${id}`)
      const driver = result.data as IStateDriver
      return serializeDriver(driver)
    } catch (e) {
      throw e.data
    }
  }

  public addDriverInfo(driverInfo: ICreateDriver): Promise<IStateDriver | any> {
    // Default to +1 if countryCode
    const validCountryCode =
      driverInfo.countryCode && driverInfo.countryCode.match(/^\+\d+$/) ? driverInfo.countryCode : '+1'
    let phone = driverInfo.phone || ''

    // Strip the country code from the phone number if it starts with the same country code
    if (phone.startsWith(validCountryCode)) {
      phone = phone.slice(validCountryCode.length)
    } else if (phone.startsWith(validCountryCode.slice(1))) {
      // Handle case where phone number might include country code without the plus
      phone = phone.slice(validCountryCode.length - 1)
    }

    // If both countryCode and phone are empty, set them to empty strings
    if (!phone) {
      phone = ''
    }
    const payload = {
      firstName: driverInfo.firstName,
      lastName: driverInfo.lastName,
      username: driverInfo.username,
      phones: [
        {
          isPrimary: true,
          countryCode: phone ? validCountryCode : '',
          phoneNumber: phone
        }
      ],
      email: {
        emailAddress: driverInfo.email
      },
      driversLicense: driverInfo.driversLicense,
      organizationId: driverInfo.organizationId || undefined,
      birthDate: driverInfo.birthDate
    }
    return serverApi.post(Paths.driver.savePersonalInfo, payload).then(({ data }) => data)
  }

  public async updateDriverInfo(driverInfo: IUpdateDriver): Promise<IStateDriver | any> {
    return await serverApi.put(Paths.driver.updatePersonalInfo, {
      id: driverInfo.id,
      firstName: driverInfo.firstName,
      lastName: driverInfo.lastName,
      username: driverInfo.username,
      email: {
        emailAddress: driverInfo.email
      },
      phones: [
        {
          // TODO: Consider more than one phone number in the future
          phoneNumber: driverInfo.phone,
          countryCode: driverInfo.countryCode,
          isPrimary: false
        }
      ],
      driversLicense: driverInfo.driversLicense,
      birthDate: driverInfo.birthDate,
      isAvailableForBackToBack: driverInfo.isAvailableForBackToBack,
      darkmode: driverInfo.darkmode
    })
  }

  public async getNearbyDrivers(location: ICoordinates) {
    const result = await serverApi.post<any[]>(Paths.dispatch.getNearbyDrivers, location).catch(e => {
      throw new Error(e.data)
    })

    return result.data.map(it => it.location)
  }

  public async getProductOperators(productIds: string[]) {
    const result = await serverApi.get<any[]>(Paths.dispatch.getProductOperators, { params: { productIds } })
    return result.data
  }

  public async getOrgOperators(orgId: string) {
    const path = Paths.dispatch.getOrgOperators.replace(':orgId', orgId)
    const result = await serverApi.get<any[]>(path)
    return result.data.filter(d => d)
  }

  public async getOperatorSchedule() {
    const path = Paths.operatorSchedule
    const result = await serverApi.get<any[]>(path)
    return result.data
  }

  public async getDriverAccumulatedHours(dto: IDriverAccumulatedHoursRequest): Promise<any> {
    const result = await serverApi.get<any[]>(Paths.driver.accumulatedHoursSummary, { params: dto })
    return result.data
  }

  public async getDriverSchedule(operatorId: string): Promise<any> {
    const result = await serverApi.get<any[]>(Paths.driver.getSchedule + '/' + operatorId)
    return result.data
  }

  public async updateDriverSchedule(dto: IDriverScheduleDTO): Promise<any> {
    const result = await serverApi.put<any[]>(Paths.driver.getSchedule, dto)
    return result.data
  }

  public async getDriverAddress(operatorId: string): Promise<any> {
    const result = await serverApi.get<any[]>(Paths.driver.getHomeAddress + '/' + operatorId)
    return result.data
  }

  public async updateDriverAddress(dto: IDriverUpdateAddressPayload): Promise<any> {
    const result = await serverApi.put<any[]>(Paths.driver.updateHomeAddress, dto)
    return result.data
  }

  public async saveDriverAddress(operatorId: string, address: IDriverAddress): Promise<any> {
    const result = await serverApi.post<any[]>(Paths.driver.saveHomeAddress + '/' + operatorId, address)
    return result.data
  }

  public async deleteDriverAddress(operatorId: string): Promise<any> {
    const result = await serverApi.delete<any[]>(Paths.driver.deleteHomeAddress + '/' + operatorId)
  }
  public async getOperatorLocation(operatorId: string) {
    const path = Paths.driver.getOperatorLocation.replace(':operatorId', operatorId)
    const result = await serverApi.get(path)
    return result.data
  }
  public async sendPushNotificationToDriver(payload: IDriverPushNotificationPayload): Promise<any> {
    const path = Paths.driver.sendPushNotificationToDriver
    const result = await serverApi.post<any>(path, payload)
    return result.data
  }
  public async computeOperatorDriveTimes({
    startDate,
    endDate,
    organizationId,
    timezone
  }: IComputeOperatorDriveTimes): Promise<any> {
    const path = Paths.trips.driveTimes
    const result = await serverApi.post<any>(path, { startDate, endDate, organizationId, timezone })
    return result.data
  }
}

const driversService = new DriversService()

export { driversService }



// WEBPACK FOOTER //
// ./src/services/Drivers.ts